import React, { useState, useEffect } from 'react'
import lang from '@/lang'
import PropTypes from 'prop-types'
import Loader from '@/modules/base/components/loader/loader'
import { makeStyles, useTheme } from '@mui/styles'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconClose from '@assets/images/icons/close.svg'
import IconCheck from '@assets/images/icons/check.svg'
import IconLogo from '@assets/images/logo/svg/logo-small-v3.svg'
import IconDatev from '@assets/images/tax-export-logos/datev.svg'
import { ButtonIconSquare } from '@/modules/base/components/buttons/index'
import clsx from 'clsx'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import request from '@/modules/base/util/request'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  loader: {
    height: '100%',
    background: 'rgba(255, 255, 255, 0.5)',
    '& > *': {
      padding: 20,
      backgroundColor: 'white',
      border: `1px solid ${theme.borderColor}`,
      borderRadius: 10,
    },
  },
  buttonContainer: {
    paddingTop: 30,
    display: 'flex',
    justifyContent: 'center',
  },
  connectionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
  },
  connectionStatus: {
    flexGrow: '1',
    margin: '0px 20px',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  connectionLine: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    width: '100%',
    transform: 'translate(-50%, -50%)',
    border: `dashed 1px ${theme.midGrey}`,
    '.connected &': {
      borderColor: theme.colors.green,
    },
  },
  connectButton: {
    background: '#f6f7f9',
    color: '#2C3335',
    '&:hover': {
      background: '#e7eced',
    },
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 50,
    width: 50,
    backgroundColor: theme.midGrey,
    border: 'none',
    borderRadius: '50%',
    zIndex: 1,
    '.connected &': {
      backgroundColor: theme.colors.green,
      cursor: 'help',
    },
  },
  notConnectedInfo: {
    display: 'flex',
    justifyContent: 'center',
    '& > p': {
      margin: 0,
      fontSize: theme.fontSizes.default,
      fontWeight: theme.fontWeights.medium,
      lineHeight: '17px',
      color: theme.textColor,
    },
  },
}))

const OAuthConnectView = props => {
  const {
    initConnectionStatus,
    authUrl,
    longTermScope,
    authStatusUrl,
    revokeUrl,
    revokeAction,
    resetUrl,
    resetAction,
    statusChanged,
    providerName,
    components,
    className,
  } = props
  const classes = useStyles()
  const theme = useTheme()

  const [showRedirectLoader, setShowRedirectLoader] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState(initConnectionStatus || {})

  useEffect(() => {
    statusChanged && statusChanged(status)
  }, [status])

  useEffect(() => {
    if (initConnectionStatus.connected) return

    setIsLoading(true)
    request(authStatusUrl, { auth: true, isConcurrent: true })
      .then(response => {
        const { data } = response.body
        setStatus(data)
      })
      .done(() => setIsLoading(false))
  }, [])

  const startAuth = (longTerm) => {
    setIsLoading(true)
    request(authUrl + (longTerm && longTermScope ? '?customScope=' + longTermScope : ''), { auth: true, isConcurrent: true })
      .then(response => {
        setShowRedirectLoader(true)
        const { data: { authUrl } } = response.body
        window.location = authUrl
      })
      .done(() => setIsLoading(false))
  }

  const onClickHandle = () => {
    startAuth()
  }

  const onClickLongTerm = () => {
    startAuth(true)
  }

  const onRevokeHandle = () => {
    setIsLoading(true)
    const revoke = revokeAction ?? request(revokeUrl, { method: 'POST', auth: true, isConcurrent: true })

    revoke()
      .then(response => {
        const { data: { successful } } = response.body
        setStatus({ connected: !successful })
      })
      .finally(() => setIsLoading(false))
  }

  const onResetHandle = () => {
    setIsLoading(true)
    const reset = resetAction ?? request(resetUrl, { method: 'POST', auth: true, isConcurrent: true })

    reset()
      .then(response => {
        const { data: { successful } } = response.body
        setStatus({ connected: !successful })
      })
      .finally(() => setIsLoading(false))
  }

  const isConnected = status?.connected
  const isLongTermToken = status?.isLongTermToken || false

  const NotConnectedInfo = (providerName) => (
    <div className={classes.notConnectedInfo}>
      <p>{lang.accounting.notConnectedText(providerName)}</p>
    </div>
  )

  const connectedInfo = isConnected
    ? (components?.ConnectedInfo && components?.ConnectedInfo(providerName, status, onRevokeHandle, onResetHandle))
    : (components?.NotConnectedInfo && components?.NotConnectedInfo(providerName)) || NotConnectedInfo(providerName)

  const providerIcon = providerName?.toLowerCase() === 'datev' ? IconDatev : null

  const showLongTermConnectButton = !isLongTermToken && isConnected && longTermScope

  return (
    <div className={clsx(classes.root, className)}>
      <div>
        <Loader customClass={classes.loader} visible={isLoading} text={lang.generics.loading} />
        <Loader customClass={classes.loader} visible={showRedirectLoader} text={lang.accounting.redirectMessage} />

        <div className={clsx(classes.connectionContainer, isConnected && 'connected')}>
          <SVGIcon width='60px' height='60px' svg={IconLogo} />
          <div className={classes.connectionStatus} >
            <div className={classes.connectionLine}></div>
            <RichTooltip heading={lang.accounting.connectionStateText} body={components?.StatusInfo(status, onRevokeHandle, onResetHandle)} width='auto' disabled={!isConnected}>
              <div className={clsx(classes.icon)}>
                { !isConnected && <SVGIcon fill={theme.white} width='20px' height='20px' svg={IconClose} /> }
                { isConnected && <SVGIcon fill={theme.white} width='20px' height='20px' svg={IconCheck} /> }
              </div>
            </RichTooltip>
          </div>
          <SVGIcon width='60px' height='60px' svg={providerIcon} />
        </div>

        {connectedInfo}

        {!isConnected
        && <div className={classes.buttonContainer}>
          <ButtonIconSquare color="custom" iconWidth="20px" className={classes.connectButton} icon={providerIcon} label={lang.accounting.datev.buttonConnectText} onClick={onClickHandle} />
        </div>
        }

        {showLongTermConnectButton
         && <div className={classes.buttonContainer}>
           <ButtonIconSquare variant='outline' label={lang.accounting.buttonLongTermConnectText} onClick={onClickLongTerm} />
         </div>
        }

      </div>
    </div>
  )
}

OAuthConnectView.propTypes = {
  authUrl: PropTypes.string.isRequired,
  longTermScope: PropTypes.string,
  authStatusUrl: PropTypes.string.isRequired,
  revokeUrl: PropTypes.string.isRequired,
  revokeAction: PropTypes.func,
  resetUrl: PropTypes.string,
  resetAction: PropTypes.func,
  providerName: PropTypes.oneOf(['DATEV', 'Addison']),
  statusChanged: PropTypes.func,
  className: PropTypes.string,
  isConnected: PropTypes.bool,
  components: PropTypes.shape({
    StatusInfo: PropTypes.func, // ({ status: PropTypes.object }) => ()
    ConnectedInfo: PropTypes.func, // ({ providerName: PropTypes.string, status: PropTypes.object, revokeAction: PropTypes.func }) => (),
    NotConnectedInfo: PropTypes.func,
  }),
}

export default OAuthConnectView
